import { Link } from "react-router-dom";
import { Bath, BedDouble, Heart, MapPin, Maximize } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { useFavorites } from "../lib/store";
import { fmtNum, fmtSAR } from "../lib/format";
import { DemoBadge } from "./Common";
import { mediaUrl } from "../lib/api";

export const PriceTag = ({ p, className = "" }) => {
  const { lang, t } = useI18n();
  return (
    <span className={className}>
      {p.price_on_request ? (lang === "ar" ? "السعر عند الطلب" : "Price on request") : fmtSAR(p.price, lang)}
      {!p.price_on_request && p.price_period && <span className="ms-1 text-xs font-normal text-fg/50">{t(p.price_period === "yearly" ? "listing.perYear" : "listing.perMonth")}</span>}
    </span>
  );
};

export function FavButton({ id, className = "" }) {
  const { isFav, toggle } = useFavorites();
  const { t } = useI18n();
  const on = isFav(id);
  return (
    <button type="button" data-testid={`favorite-toggle-${id}`} aria-pressed={on} aria-label={on ? t("property.removeFav") : t("property.saveFav")}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(id); }}
      className={`flex h-10 w-10 items-center justify-center rounded-full border backdrop-blur-md transition-[background-color,border-color,transform] duration-300 hover:scale-110 ${on ? "border-cta bg-cta text-canvas" : "border-fg/30 bg-canvas/50 text-fg"} ${className}`}>
      <Heart className={`h-4 w-4 ${on ? "fill-current" : ""}`} />
    </button>
  );
}

const Specs = ({ p }) => {
  const { lang, t } = useI18n();
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-fg/60">
      {p.bedrooms > 0 && <span className="flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5 text-cta" />{fmtNum(p.bedrooms, lang)} {t("listing.beds")}</span>}
      {p.bathrooms > 0 && <span className="flex items-center gap-1.5"><Bath className="h-3.5 w-3.5 text-cta" />{fmtNum(p.bathrooms, lang)} {t("listing.baths")}</span>}
      <span className="flex items-center gap-1.5"><Maximize className="h-3.5 w-3.5 text-cta" />{fmtNum(p.size_sqm, lang)} {t("listing.sqm")}</span>
    </div>
  );
};

export default function PropertyCard({ p, layout = "grid" }) {
  const { t, tr } = useI18n();
  const { cityName, nbName, label } = useSite();
  const list = layout === "list";
  return (
    <Link to={`/properties/${p.slug}`} data-testid={`property-card-${p.slug}`}
      className={`group relative flex overflow-hidden rounded-sm border border-fg/10 bg-surface transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1 hover:border-cta/50 hover:shadow-[0_30px_60px_-30px_rgba(201,169,110,0.35)] ${list ? "flex-col sm:flex-row" : "flex-col"}`}>
      <div className={`relative overflow-hidden ${list ? "aspect-[4/3] sm:aspect-auto sm:w-80 sm:shrink-0" : "aspect-[4/3]"}`}>
        <img src={mediaUrl(p.images?.[0])} alt={tr(p, "title")} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-canvas/80 via-transparent to-transparent" />
        <div className="absolute start-3 top-3 flex flex-wrap gap-2">
          {p.is_demo && <DemoBadge testid={`demo-badge-${p.slug}`} />}
          <span className="rounded-sm bg-canvas/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-cta backdrop-blur">{t(`purpose.${p.purpose}`)}</span>
        </div>
        <FavButton id={p.id} className="absolute end-3 top-3" />
        <PriceTag p={p} className="absolute bottom-3 start-4 font-display text-2xl font-semibold text-fg" />
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <p className="text-[11px] uppercase tracking-[0.2em] text-cta/80">{label("property_type", p.type)} · {t(`listing.${p.completion_status}`)}</p>
        <h3 className="font-display text-xl leading-snug text-fg transition-colors group-hover:text-cta">{tr(p, "title")}</h3>
        <p className="flex items-center gap-1.5 text-xs text-fg/55"><MapPin className="h-3.5 w-3.5" />{nbName(p.city, p.neighborhood)}, {cityName(p.city)}</p>
        {list && <p className="line-clamp-2 text-sm text-fg/55">{tr(p, "description")}</p>}
        <div className="mt-auto border-t border-fg/10 pt-3"><Specs p={p} /></div>
      </div>
    </Link>
  );
}
